import React from 'react';
import type { PackageOffer, Guide } from '../../types';

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  consultation: 'Consultation',
  preparation: 'Preparation',
  integration: 'Integration',
  journey: 'Journey Session',
  ceremony: 'Ceremony',
  follow_up: 'Follow-up',
  additional: 'Additional Support'
};

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const formatCurrency = (cents: number) => currencyFormatter.format(cents / 100);

const formatDate = (value: string) => {
  try {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
  } catch {
    // fall through
  }
  return value;
};

const formatSessionDateTime = (dateValue?: string | null, timeValue?: string | null): string | null => {
  if (!dateValue) {
    return null;
  }

  try {
    const isoString = timeValue ? `${dateValue}T${timeValue}` : dateValue;
    const date = new Date(isoString);
    if (!Number.isNaN(date.getTime())) {
      const dateLabel = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
      if (timeValue) {
        const timeLabel = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        return `${dateLabel} • ${timeLabel}`;
      }
      return dateLabel;
    }
  } catch {
    // fall back to raw string below
  }

  return timeValue ? `${dateValue} • ${timeValue}` : dateValue;
};

const formatDurationLabel = (minutes?: number | null): string | null => {
  if (!minutes || minutes <= 0) return null;
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${minutes} minute${minutes === 1 ? '' : 's'}`;
};

type PackageDetailsModalProps = {
  offer: PackageOffer;
  guide?: Guide | null;
  onClose: () => void;
};

export const PackageDetailsModal: React.FC<PackageDetailsModalProps> = ({ offer, guide, onClose }) => {
  const fulfilledCount = offer.units.filter((u) => u.status === 'fulfilled').length;
  const totalUnits = offer.units.length;
  const progressPercent = totalUnits > 0 ? (fulfilledCount / totalUnits) * 100 : 0;
  const remainingUnits = totalUnits - fulfilledCount;

  const isPurchased = offer.status === 'accepted';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div
        className="relative flex h-full max-h-[90vh] w-full max-w-md flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100"
          >
            <svg
              width="18"
              height="20"
              viewBox="0 0 18 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
            >
              <path d="M17 10H1M1 10L8 17M1 10L8 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <h1 className="text-lg font-normal text-gray-900">Package Details</h1>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center text-2xl text-gray-600 hover:bg-gray-100 rounded-lg"
            onClick={onClose}
          >
            ?
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50">
          {/* Package Info Section */}
          <section className="bg-white px-4 py-4">
            <div className="flex items-start gap-3">
              {guide?.image ? (
                <img src={guide.image} alt={guide.name} className="h-12 w-12 rounded-full object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 text-gray-600">
                  <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-lg font-normal leading-7 text-gray-900">{offer.name}</h3>
                {guide && (
                  <p className="text-sm leading-5 text-gray-600 ph-mask">
                    with {guide.name}
                  </p>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4 rounded-lg bg-gray-100 px-3 py-3">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="text-gray-600">Progress</span>
                <span className="text-gray-900">
                  {fulfilledCount} of {totalUnits} sessions
                </span>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-300">
                <div
                  className="h-full bg-gray-700 transition-all"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>

            {/* Stats */}
            <div className="mt-4 flex items-center justify-between">
              <div className="flex-1 text-center">
                <p className="text-2xl font-normal leading-8 text-gray-900">{totalUnits}</p>
                <p className="text-xs leading-4 text-gray-600">Total Sessions</p>
              </div>
              <div className="flex-1 text-center">
                <p className="text-2xl font-normal leading-8 text-gray-900">{fulfilledCount}</p>
                <p className="text-xs leading-4 text-gray-600">Completed</p>
              </div>
              <div className="flex-1 text-center">
                <p className="text-2xl font-normal leading-8 text-gray-900">{remainingUnits}</p>
                <p className="text-xs leading-4 text-gray-600">Remaining</p>
              </div>
            </div>
          </section>

          {/* Sessions List */}
          <section className="mt-3 bg-white px-4 py-4">
            <h3 className="mb-4 text-lg font-normal text-gray-900">Sessions</h3>
            <div className="space-y-3">
              {offer.units.map((unit, index) => {
                const isCompleted = unit.status === 'fulfilled';
                const isScheduled = unit.status === 'scheduled';
                const isFuture = unit.status === 'unallocated';
                const typeLabel = COMPONENT_TYPE_LABELS[unit.componentType.toLowerCase()] || unit.componentType;
                const sessionDateLabel = formatSessionDateTime(unit.sessionDate, unit.sessionTime);
                const durationLabel = formatDurationLabel(unit.durationMinutes);

                return (
                  <div
                    key={unit.id}
                    className={`flex items-center gap-3 rounded-lg border px-3 py-3 ${
                      isFuture ? 'border-dashed border-gray-300 bg-gray-50' : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div
                      className={`flex h-8 w-8 items-center justify-center rounded-full ${
                        isCompleted || isScheduled
                          ? 'bg-gray-700'
                          : 'bg-gray-300'
                      }`}
                    >
                      {isCompleted || isScheduled ? (
                        <svg
                          className="h-3.5 w-3.5 text-white"
                          fill="none"
                          viewBox="0 0 14 14"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2 7l3.5 3.5L12 3" />
                        </svg>
                      ) : (
                        <span className="text-sm font-normal text-gray-600">{index + 1}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className={`text-base leading-6 ${isFuture ? 'text-gray-600' : 'text-gray-900'}`}>
                        {unit.componentName || typeLabel}
                      </p>
                      <p className={`text-sm leading-5 ${isFuture ? 'text-gray-500' : 'text-gray-600'}`}>
                        {sessionDateLabel ?? 'Date TBD'} • {durationLabel ?? 'Duration TBD'}
                      </p>
                    </div>
                    <div className="flex h-4 w-4 items-center justify-center">
                      {isFuture ? (
                        <svg className="h-4 w-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 10 16" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1l8 7-8 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Purchase Information - Only show if accepted */}
          {isPurchased && (
            <section className="mt-3 bg-white px-4 py-4">
              <h3 className="mb-4 text-lg font-normal text-gray-900">Purchase Information</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-base">
                  <span className="text-gray-600">Package Price</span>
                  <span className="text-gray-900">{formatCurrency(offer.totalCents)}</span>
                </div>
                <div className="flex items-center justify-between text-base">
                  <span className="text-gray-600">Purchase Date</span>
                  <span className="text-gray-900">{formatDate(offer.createdAt)}</span>
                </div>
                <div className="flex items-center justify-between text-base">
                  <span className="text-gray-600">Payment Method</span>
                  <span className="text-gray-900">•••• ••••</span>
                </div>
                <div className="flex items-center justify-between text-base">
                  <span className="text-gray-600">Transaction ID</span>
                  <span className="text-gray-900">#{offer.id.slice(0, 12)}</span>
                </div>
              </div>
              <button
                type="button"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 py-3 text-base font-normal text-white hover:bg-gray-800 active:bg-gray-700"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Download Receipt
              </button>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default PackageDetailsModal;
